import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import {CheckCircle, Loader} from 'lucide-react'

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'checking' | 'success' | 'error'>('checking')
  const [invoice, setInvoice] = useState<any>(null)
  const sessionId = searchParams.get('session_id')

  useEffect(() => {
    if (!sessionId) {
      navigate('/')
      return
    }

    checkPaymentStatus()
  }, [sessionId])

  const checkPaymentStatus = async () => {
    let attempts = 0
    const maxAttempts = 10

    const pollStatus = async () => {
      try {
        const result = await lumi.entities.invoices.list({
          filter: { stripeSessionId: sessionId }
        })

        if (result.list.length > 0 && result.list[0].status === 'paid') {
          setInvoice(result.list[0])
          setStatus('success')
          return
        }

        attempts++
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000)
        } else {
          setStatus('error')
        }
      } catch (error) {
        console.error('Error checking payment status:', error)
        attempts++
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000)
        } else {
          setStatus('error')
        }
      }
    }

    pollStatus()
  }

  if (status === 'checking') {
    return (
      <div className="min-h-screen pt-24 pb-16 px-4 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-16 h-16 animate-spin text-[#0B5394] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment...</h2>
          <p className="text-gray-600">Please wait while we confirm your payment</p>
        </div>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-900 mb-4">Payment Verification Failed</h2>
            <p className="text-gray-600 mb-6">
              We couldn't verify your payment. Please contact support if you were charged.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-[#0B5394] hover:bg-[#084273] text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 bg-gradient-to-b from-green-50 to-white">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl border-2 border-green-500 p-8 text-center">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Payment Successful!</h1>
          <p className="text-lg text-gray-600 mb-8">
            Your invoice has been paid and marked as complete.
          </p>

          {invoice && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Invoice Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Invoice Number</p>
                  <p className="font-semibold text-gray-900">{invoice.invoiceNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Business</p>
                  <p className="font-semibold text-gray-900">{invoice.businessName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Client</p>
                  <p className="font-semibold text-gray-900">{invoice.clientName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Amount Paid</p>
                  <p className="font-semibold text-green-600 text-lg">
                    {invoice.currency} {invoice.totalAmount.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate('/')}
              className="bg-[#0B5394] hover:bg-[#084273] text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Create Another Invoice
            </button>
            <button
              onClick={() => navigate('/invoices')}
              className="bg-[#047857] hover:bg-[#059669] text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              View Invoice History
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentSuccessPage